import { Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService, getPollResultsAPI } from '../api/api.service';
import { Ballot } from '../ballot/backend-types';
import { ElectorDataService } from './elector-data.service';

@Injectable({
  providedIn: 'root'
})
export class EventResultsService {

  eventResultList: ChoiceCountByStatement[] = [];
  displaySpinner = signal<boolean>(false);

  constructor(
    private apiService: ApiService,
    private userService: ElectorDataService
    ) { }

  async getResults(event: Ballot): Promise<void> {
    if(event.voteEventData.showResults){
      try {
        this.displaySpinner.set(true);
        let counts = await firstValueFrom(this.apiService.postWithHandshakeCheck<ChoiceCountForEvent>(getPollResultsAPI, {
          voter_id: this.userService.user?.voter_id,
          eventId: event.eventId
        }))
        this.displaySpinner.set(false);
        if(counts.body) {
          this.updateEventsResults(this.organizeByStatement(counts.body));
        } else {
          throw new Error("NO_RESULTS");
        }
      } catch(err) {
        this.displaySpinner.set(false);
        throw(err); 
      }
    } else {
      throw new Error("NO_RESULTS");
    }
  }

  private organizeByStatement(result: ChoiceCountForEvent): ChoiceCountByStatement {
    result.result.forEach(r => {
      if(r.choiceId.length === 0) {
        r.choiceId = "noID";
        r.choiceDescription = "No Response";
        r.label = "No Response";
        r.value = r.choiceCount;
      }
    })
    result.result = this.sortResults(result);
    let choiceCounts: ChoiceCountByStatement = {
      eventId: result.eventId,
      statements: []
    }
    let allStatements: string[] = [];
    let i = -1;
    result.result.forEach((choice) => {
      if( !allStatements.includes(choice.statementId)) {
        i++;
        allStatements.push(choice.statementId);
        choiceCounts.statements.push({
          statementId: choice.statementId,
          statementDescription: choice.statementDescription,
          choices: []
        })
      }
      choiceCounts.statements[i].choices.push({
        choiceId: choice.choiceId,
        choiceDescription: choice.choiceDescription,
        choiceCount: choice.choiceCount,
        choiceCountPerc: choice.choiceCountPerc,
        writeIns: choice.writeIns
      })
    })
    return choiceCounts;
  }

  sortResults(result: ChoiceCountForEvent): ChoiceCount[] {
    return result.result.sort((a,b) => this.compareIDs(a, b));
  }

  // Returns -1 if a < b
  // Returns  1 if a > b:
  // Compare first by statement ID, then by contest ID.
  // In the case of blank submissions, the contest ID is "No ID" and 
  // Any Voatz ContestID < "No ID".
  compareIDs(a: ChoiceCount, b: ChoiceCount): number {
    let compare = 0;
    compare = a.statementId.localeCompare(b.statementId);
    if(compare === 0) {
      return a.choiceId.localeCompare(b.choiceId);
    } else {
      return compare;
    }
  }

  updateEventsResults(result: ChoiceCountByStatement) {
    let resultIndex = this.eventResultList.findIndex((r) => r.eventId === result.eventId);
    if( resultIndex > -1) {
      this.eventResultList[resultIndex] = result;
    } else {
      this.eventResultList.push(result);
    }
  }

  getEventResults(eventId: number): ChoiceCountByStatement | undefined {
    return this.eventResultList.find((r) => r.eventId === eventId);
  }

  getStatementResults(eventId: number, statementId: string): ChoiceCountForStatement | undefined {
    return this.eventResultList.find((r) => r.eventId === eventId)?.statements.find((s) => s.statementId === statementId);
  }

}

export interface ChoiceCount {
  eventId: number;
  questionId: string;
  questionDescription: string;
  statementId: string;
  statementDescription: string;
  choiceId: string;
  choiceDescription: string;
  choiceCount: number;
  choiceCountPerc?: number;
  label?: string;
  value?: number;
  priority?: number;
  writeIns: string[];
}

export interface ChoiceCountForEvent {
  eventId: number;
  result: ChoiceCount[];
}

export interface ChoiceCountByStatement{
  eventId: number;
  statements: ChoiceCountForStatement[];
}

export interface ChoiceCountForStatement {
  statementId: string;
  statementDescription: string;
  choices: {
    choiceId: string;
    choiceDescription: string;
    choiceCount: number;
    choiceCountPerc?: number;
    writeIns: string[];
  }[]
}